// Kept equivalent in frontend/src/lib/feedback.ts and mobile/src/lib/feedback.ts.
// Memory only: pending operations and callbacks must never be written to disk.
export type FeedbackTone = 'loading' | 'success' | 'warning' | 'error'
export type FeedbackAction = { label: string; run: () => void }
export type FeedbackItem = {
  id: number
  key?: string
  tone: FeedbackTone
  title: string
  detail?: string
  /** Fraction of confirmed work, not elapsed time. Undefined means indeterminate. */
  progress?: number
  action?: FeedbackAction
}

export function createFeedbackStore() {
  let nextId = 0
  let items: FeedbackItem[] = []
  const listeners = new Set<() => void>()
  const timers = new Map<number, ReturnType<typeof setTimeout>>()
  const emit = () => listeners.forEach((listener) => listener())
  const clearTimer = (id: number) => {
    clearTimeout(timers.get(id))
    timers.delete(id)
  }
  const dismiss = (id: number) => {
    clearTimer(id)
    items = items.filter((item) => item.id !== id)
    emit()
  }
  const update = (id: number, patch: Partial<Omit<FeedbackItem, 'id'>>) => {
    if (!items.some((item) => item.id === id)) return
    clearTimer(id)
    items = items.map((item) => item.id === id ? { ...item, ...patch } : item)
    const item = items.find((item) => item.id === id)!
    if (item.tone === 'success') timers.set(id, setTimeout(() => dismiss(id), 4000))
    emit()
  }
  const show = (tone: FeedbackTone, title: string, detail?: string, action?: FeedbackAction, key?: string) => {
    if (key) items.filter((item) => item.key === key).forEach((item) => dismiss(item.id))
    const id = ++nextId
    items = [...items, { id, key, tone, title, detail, action }]
    update(id, {})
    return {
      id,
      update: (patch: Pick<Partial<FeedbackItem>, 'title' | 'detail' | 'progress'>) => update(id, patch),
      success: (title: string, detail?: string) => update(id, { tone: 'success', title, detail, progress: 1, action: undefined }),
      warning: (title: string, detail?: string, action?: FeedbackAction) => update(id, { tone: 'warning', title, detail, progress: undefined, action }),
      error: (title: string, detail?: string, action?: FeedbackAction) => update(id, { tone: 'error', title, detail, progress: undefined, action }),
      dismiss: () => dismiss(id),
    }
  }
  return {
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener) } },
    getSnapshot: () => items,
    dismiss,
    clear: () => { timers.forEach(clearTimeout); timers.clear(); items = []; emit() },
    success: (title: string, detail?: string) => show('success', title, detail),
    warning: (title: string, detail?: string) => show('warning', title, detail),
    error: (title: string, detail?: string) => show('error', title, detail),
    start: (title: string, detail?: string, key?: string) => show('loading', title, detail, undefined, key),
  }
}

export const feedback = createFeedbackStore()
